'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { getInternalNotesQueryKey } from 'src/shared/queries/internal-notes/useInternalNotes';
import { InternalNote, InternalNoteId } from 'src/shared/types/internalNote.types';

export function useCreateInternalNote(integrationId?: string, threadId?: string) {
  const { orgFetch, currentOrg } = useOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const res = await orgFetch<InternalNote>('/api/v2/internal-notes', {
        method: 'POST',
        body: JSON.stringify({ integrationId, threadId, content }),
      });

      if (!res.ok) {
        throw new Error('Failed to create internal note');
      }

      return res.json();
    },
    onSuccess: () => {
      if (integrationId && threadId) {
        queryClient.invalidateQueries({
          queryKey: getInternalNotesQueryKey(currentOrg.id, integrationId, threadId),
        });
      }
    },
  });
}

export function useUpdateInternalNote(integrationId?: string, threadId?: string) {
  const { orgFetch, currentOrg } = useOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, content }: { noteId: InternalNoteId; content: string }) => {
      const res = await orgFetch<InternalNote>(`/api/v2/internal-notes/${noteId}`, {
        method: 'PATCH',
        body: JSON.stringify({ content }),
      });

      if (!res.ok) {
        if (res.status === 403) throw new Error('Only the author can edit this note');
        if (res.status === 404) throw new Error('Note not found');
        throw new Error('Failed to update internal note');
      }

      return res.json();
    },
    onSuccess: () => {
      if (integrationId && threadId) {
        queryClient.invalidateQueries({
          queryKey: getInternalNotesQueryKey(currentOrg.id, integrationId, threadId),
        });
      }
    },
  });
}

export function useDeleteInternalNote(integrationId?: string, threadId?: string) {
  const { orgFetch, currentOrg } = useOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: InternalNoteId) => {
      const res = await orgFetch(`/api/v2/internal-notes/${noteId}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        if (res.status === 403) throw new Error('Only the author can delete this note');
        if (res.status === 404) throw new Error('Note not found');
        throw new Error('Failed to delete internal note');
      }
    },
    onSuccess: () => {
      if (integrationId && threadId) {
        queryClient.invalidateQueries({
          queryKey: getInternalNotesQueryKey(currentOrg.id, integrationId, threadId),
        });
      }
    },
  });
}
